<template>
    <header class="custom-app-bar d-flex align-center justify-center px-2 px-sm-4">
        <div class="header-inner-container d-flex align-center w-100">
            <!-- Logo -->
            <a class="logo ml-1 ml-sm-2 mr-1 mr-sm-3" @click.prevent="navigateToRoute('/')" style="cursor: pointer;">
                <img class="img-fluid" src="/img/logo.png" :alt="terms.logo_alt || 'Avem'" />
            </a>

            <v-spacer></v-spacer>

            <!-- Main Menu (Desktop >= 960px) -->
            <ul v-if="$vuetify.display.mdAndUp" class="main-menu">
                <li v-for="(item, i) in items" :key="i">
                    <a :href="item.href" @click.prevent="handleMenuClick(item.href)">{{ item.title }}</a>
                </li>
            </ul>

            <v-spacer v-if="$vuetify.display.mdAndUp"></v-spacer>

            <!-- Right Side Actions Group -->
            <div class="header-actions-group d-inline-flex align-center mr-1 mr-sm-2">
                <!-- Mobile/Tablet Drawer Toggle Icon (< 960px) -->
                <button v-if="$vuetify.display.smAndDown" class="popping-button header-drawer-btn mr-1 pa-1 d-inline-flex align-center justify-center" @click.stop="drawer = !drawer" title="Toggle Menu">
                    <v-icon color="#ffc451" class="drawer-icon">mdi-menu</v-icon>
                </button>

                <!-- Cart / Checkout Button with Counter & Accumulated Total -->
                <div v-if="!isCartHidden" class="cart-header-wrapper d-inline-flex align-center cursor-pointer ml-1 mr-1 mr-sm-2" @click="navigateToRoute('/order')" :title="terms.cart_title">
                    <button id="header-cart-btn" class="cart-btn pa-1">
                        <v-badge :content="cartItemCount" :model-value="cartItemCount > 0" color="#ffc451" text-color="black"
                            offset-x="-4" offset-y="-4">
                            <v-icon color="#ffc451" class="cart-header-icon">mdi-cart-outline</v-icon>
                        </v-badge>
                    </button>
                    <div v-if="cartItemCount > 0" class="cart-header-total-pill ml-1 px-2 py-1 rounded-pill bg-black-transparent d-none d-sm-flex align-center">
                        <span class="gold-amount-text font-weight-bold text-caption text-sm-body-2">
                            {{ formatNumber(cartTotalEuro) }} {{ terms.currency_euro || 'EUR' }}
                        </span>
                    </div>
                </div>

                <!-- Follow Us / Social Share Menu -->
                <v-menu location="bottom end" transition="scale-transition" :z-index="100000">
                    <template #activator="{ props }">
                        <button class="popping-button social-share-btn mr-1" v-bind="props" :title="terms.follow_us_on || 'Follow us'">
                            <v-icon color="#ffc451" class="social-share-icon">mdi-share-variant-outline</v-icon>
                        </button>
                    </template>
                    <v-list class="social-dropdown-list py-2 px-1 bg-black-card border-gold">
                        <v-list-item link @click="navigateToLink('https://www.facebook.com/profile.php?id=100094178810701')" class="social-menu-item">
                            <template #prepend>
                                <i class="fab fa-facebook-square social-menu-icon fb-color mr-2"></i>
                            </template>
                            <v-list-item-title class="font-weight-medium text-white">Facebook</v-list-item-title>
                        </v-list-item>
                        <v-list-item link @click="navigateToLink('https://www.instagram.com/avem.offi/')" class="social-menu-item">
                            <template #prepend>
                                <i class="fab fa-instagram social-menu-icon ig-color mr-2"></i>
                            </template>
                            <v-list-item-title class="font-weight-medium text-white">Instagram</v-list-item-title>
                        </v-list-item>
                        <v-list-item link @click="navigateToLink('https://www.tiktok.com/@avem.official?lang=en')" class="social-menu-item">
                            <template #prepend>
                                <i class="fab fa-tiktok social-menu-icon tt-color mr-2"></i>
                            </template>
                            <v-list-item-title class="font-weight-medium text-white">TikTok</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>

                <!-- Language Switcher Dropdown Menu -->
                <v-menu location="bottom end" transition="scale-transition" :z-index="100000">
                    <template #activator="{ props }">
                        <button class="popping-button language-dropdown-btn" v-bind="props" :title="terms.select_language || 'Select Language'">
                            <v-icon color="#ffc451" class="language-dropdown-icon">mdi-web</v-icon>
                        </button>
                    </template>
                    <v-list class="social-dropdown-list py-2 px-1 bg-black-card border-gold">
                        <v-list-item link @click="selectLanguage('bg')" class="social-menu-item" :class="{ 'active-lang-item': pickedLanguage === 'bg' }">
                            <template #prepend>
                                <span class="fi fi-bg lang-menu-flag mr-2"></span>
                            </template>
                            <v-list-item-title class="font-weight-medium text-white">{{ terms.language_bg || 'Bulgarian' }}</v-list-item-title>
                        </v-list-item>
                        <v-list-item link @click="selectLanguage('en')" class="social-menu-item" :class="{ 'active-lang-item': pickedLanguage === 'en' }">
                            <template #prepend>
                                <span class="fi fi-gb lang-menu-flag mr-2"></span>
                            </template>
                            <v-list-item-title class="font-weight-medium text-white">English</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </div>
        </div>
    </header>

    <v-navigation-drawer v-if="$vuetify.display.smAndDown" v-model="drawer" location="top" temporary class="custom-mobile-drawer">
        <div class="mobile-drawer-content py-0">
            <v-list class="bg-transparent py-0 pa-0">
                <v-list-item v-for="(item, i) in items" :key="i" link @click="handleMenuClick(item.href)" class="mobile-drawer-item py-3 px-4">
                    <v-list-item-title class="text-center font-weight-bold text-uppercase mobile-drawer-text">
                        {{ item.title }}
                    </v-list-item-title>
                </v-list-item>
            </v-list>
        </div>
    </v-navigation-drawer>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useDisplay } from 'vuetify';
import 'flag-icons/css/flag-icons.min.css';


const router = useRouter();
const route = useRoute();
const store = useStore();
const display = useDisplay();

// Reactive Data
const drawer = ref(false);

const isCartHidden = computed(() => {
    return route.path === '/order' || route.name === 'order' || route.path === '/offers' || route.name === 'offers';
});

// Computed Properties
const headerHeight = computed(() => {
    if (display.xs.value) return 52;
    if (display.sm.value) return 60;
    return 72;
});
const pickedLanguage = computed(() => store.state.languageCode);
const terms = computed(() => store.state.terms);
const cartItemCount = computed(() => store.getters.cartItemCount);
const cartTotalEuro = computed(() => store.getters.cartTotalEuro);

const formatNumber = (val) => {
    if (val === undefined || val === null || isNaN(val)) return '0,00';
    return Number(val).toFixed(2).replace('.', ',');
};

const items = computed(() => {
    const isEn = store.state.languageCode === 'en';
    const prefix = isEn ? '/en' : '';
    return [
        { title: terms.value.menu_home, href: `${prefix}/#hero` },
        { title: terms.value.menu_about, href: `${prefix}/#about` },
        { title: terms.value.offers || 'Products', href: `${prefix}/offers` },
        { title: terms.value.menu_gallery, href: `${prefix}/#portfolio` },
        { title: terms.value.menu_contact, href: `${prefix}/#contact` },
    ];
});

// Methods
const selectLanguage = (langCode) => {
    if (store.state.languageCode === langCode) return;
    store.commit('setLanguageCode', langCode);

    const currentPath = route.path;
    let targetPath = currentPath;
    if (langCode === 'en') {
        if (!currentPath.startsWith('/en')) {
            targetPath = currentPath === '/' ? '/en' : `/en${currentPath}`;
        }
    } else {
        if (currentPath.startsWith('/en')) {
            targetPath = currentPath.replace(/^\/en/, '') || '/';
        }
    }
    router.push({ path: targetPath, query: route.query, hash: route.hash }).then(() => {
        router.go(0); // Reload with fresh language terms
    });
};

const toggleLanguage = () => {
    const nextLang = pickedLanguage.value === "en" ? "bg" : "en";
    selectLanguage(nextLang);
};

const navigateToLink = (url) => {
    event.preventDefault();
    window.open(url, '_blank', 'noopener,noreferrer');
};

const navigateToRoute = (target) => {
    const isEn = store.state.languageCode === 'en';
    let path = target;
    if (isEn && target === '/') {
        path = '/en';
    } else if (isEn && !target.startsWith('/en')) {
        path = `/en${target}`;
    }

    if (path === '/' || path === '/en') {
        if ((router.currentRoute.value.path === '/' || router.currentRoute.value.path === '/en') && (!router.currentRoute.value.hash || router.currentRoute.value.hash === '#hero')) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        router.push({ path, hash: '#hero' });
        return;
    }
    router.push(path);
};

const handleMenuClick = (href) => {
    drawer.value = false;
    const isEn = store.state.languageCode === 'en';
    const homePath = isEn ? '/en' : '/';

    if (href === `${homePath}#hero` || href === '/#hero' || href === '/' || href === '/en') {
        if ((router.currentRoute.value.path === homePath || router.currentRoute.value.path === '/') && (!router.currentRoute.value.hash || router.currentRoute.value.hash === '#hero')) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        router.push({ path: homePath, hash: '#hero' });
        return;
    }

    if (href.includes('#')) {
        const hash = `#${href.split('#')[1]}`;
        if (router.currentRoute.value.path === homePath && router.currentRoute.value.hash === hash) {
            const el = document.querySelector(hash);
            if (el) {
                const headerHeight = window.innerWidth < 600 ? 56 : (window.innerWidth < 960 ? 64 : 76);
                const topPos = window.pageYOffset + el.getBoundingClientRect().top - headerHeight;
                window.scrollTo({ top: Math.max(0, topPos), behavior: 'smooth' });
            }
        } else {
            router.push({ path: homePath, hash });
        }
    } else {
        router.push(href);
    }
};

</script>

<style scoped>
/*TPL-HEADER*/

.cart-btn {
    background: transparent;
    border: none;
    padding: 4px 8px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s ease;
}

.cart-btn:hover {
    transform: scale(1.15);
}

.cart-header-wrapper {
    transition: transform 0.2s ease;
    flex-shrink: 0 !important;
}

.cart-header-wrapper:hover {
    transform: scale(1.04);
}

.cart-header-total-pill {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    letter-spacing: 0.3px;
    white-space: nowrap !important;
    flex-shrink: 0 !important;
}

.header-inner-container {
    max-width: 1200px !important;
    margin-left: auto !important;
    margin-right: auto !important;
    height: 100%;
    position: relative;
}

@media (min-width: 1650px) {
    .logo {
        position: absolute !important;
        right: 100% !important;
        top: 50% !important;
        transform: translateY(-50%) !important;
        margin-right: 0 !important;
        padding-right: 12px !important;
    }

    .header-actions-group {
        position: absolute !important;
        left: 100% !important;
        top: 50% !important;
        transform: translateY(-50%) !important;
        margin-left: 0 !important;
        padding-left: 12px !important;
    }
}

.custom-app-bar {
    height: 72px !important;
    min-height: 72px !important;
    max-height: 72px !important;
    background-color: #000000 !important;
    border-bottom: 1px solid rgba(255, 196, 81, 0.15) !important;
    box-shadow: none !important;
    z-index: 1010 !important;
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    width: 100% !important;
}

.cart-header-icon {
    font-size: 28px !important;
    transition: font-size 0.3s ease;
}

.gold-amount-text {
    color: #ffc451 !important;
    font-size: 0.9rem;
    white-space: nowrap !important;
    transition: font-size 0.3s ease;
}

.social-share-icon,
.language-dropdown-icon {
    font-size: 26px !important;
    transition: font-size 0.3s ease, transform 0.25s ease;
}

.social-share-btn:hover .social-share-icon {
    transform: scale(1.15) rotate(12deg);
}

.language-dropdown-btn:hover .language-dropdown-icon {
    transform: scale(1.15) rotate(-12deg);
}

.bg-black-card {
    background: #111111 !important;
    border: 1px solid rgba(255, 196, 81, 0.4) !important;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.8) !important;
    border-radius: 12px !important;
}

.social-menu-item {
    transition: background 0.2s ease, transform 0.2s ease;
    border-radius: 8px !important;
    margin: 2px 4px !important;
}

.social-menu-item:hover {
    background: rgba(255, 196, 81, 0.15) !important;
    transform: translateX(3px);
}

.social-menu-icon {
    width: 24px;
    text-align: center;
}

.fb-color {
    color: #1877F2 !important;
    font-size: 20px;
}

.ig-color {
    background: linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    font-size: 20px;
}

.tt-color {
    color: #69C9D0 !important;
    font-size: 20px;
}

.lang-flag-icon {
    font-size: 20px;
    border-radius: 3px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
    display: inline-block;
}

.lang-menu-flag {
    font-size: 18px;
    border-radius: 2px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
    display: inline-block;
}

.active-lang-item {
    background: rgba(255, 196, 81, 0.25) !important;
    border: 1px solid rgba(255, 196, 81, 0.5) !important;
}

.cart-btn :deep(.v-badge__badge) {
    font-size: 11px !important;
    font-weight: 800 !important;
    height: 14px !important;
    min-width: 14px !important;
    padding: 0 2px !important;
    border-radius: 50% !important;
    line-height: 14px !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.4) !important;
}

.popping-button {
    border: none;
    padding: 4px;
    border-radius: 6px;
    font-size: 24px;
    cursor: pointer;
    text-align: center;
    transition: transform 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275), filter 0.25s ease, font-size 0.3s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0 5px;
    background: transparent;
}

.popping-button:hover {
    transform: translateY(-3px) scale(1.25);
    filter: drop-shadow(0 4px 10px rgba(255, 196, 81, 0.6));
}

.popping-button.facebook:hover {
    filter: drop-shadow(0 4px 10px rgba(24, 119, 242, 0.8));
}

.popping-button.instagram:hover {
    filter: drop-shadow(0 4px 10px rgba(225, 48, 108, 0.8));
}

.popping-button.tiktok:hover {
    filter: drop-shadow(0 4px 10px rgba(105, 201, 208, 0.8));
}

.popping-button.facebook {
    color: #007bff;
}

.popping-button.instagram {
    background: linear-gradient(217deg,
            #000000,
            /* Black */
            #000000,
            /* Black */
            #405DE6,
            /* Royal Blue */
            #833AB4,
            /* Purple */
            #FD1D1D,
            /* Red */
            #F77737,
            /* Orange */
            #FFDC80,
            /* Yellow */
            #000000,
            /* Black */
            #000000
            /* Black */
        );
    -webkit-background-clip: text;
    background-clip: text;
    /* Clips the background to the text */
    -webkit-text-fill-color: transparent;
    /* Makes the text transparent to show the background */
}

.popping-button.tiktok {
    background: linear-gradient(135deg,
            #000000,
            /* Black */
            #69C9D0,
            /* Turquoise */
            #FFFFFF,
            /* White */
            #EE1D52,
            /* Red */
            #000000
            /* Black */
        );
    -webkit-background-clip: text;
    background-clip: text;
    /* Clips the background to the text */
    -webkit-text-fill-color: transparent;
    /* Makes the text transparent to show the background */
}

@keyframes pulseFont {
    0% {
        font-size: 12px;
        /* Starting font size */
    }

    50% {
        font-size: 18px;
        /* Larger font size */
    }

    100% {
        font-size: 12px;
        /* Return to starting font size */
    }
}

.pulsing-button {
    width: 66px;
    color: #ffc451;
    border: none;
    padding: 0px;
    border-radius: 1px;
    cursor: pointer;
    text-align: center;
    transition: background-color 0.2s ease;
    animation: pulseFont 1.5s infinite;
    /* Apply the animation */
    transition: transform 0.3s ease;
}


@keyframes pulseGlare {
    0% {
        box-shadow: 0 0 15px rgba(255, 196, 81, 0.5);
    }

    10% {
        box-shadow: 0 0 25px rgba(255, 196, 81, 0.6);
    }

    30% {
        box-shadow: 0 0 35px rgba(255, 196, 81, 0.7);
    }

    40% {
        box-shadow: 0 0 45px rgba(255, 196, 81, 0.8);
    }

    50% {
        box-shadow: 0 0 55px rgba(255, 196, 81, 0.9);
    }

    60% {
        box-shadow: 0 0 55px rgba(255, 196, 81, 0.9);
    }

    70% {
        box-shadow: 0 0 45px rgba(255, 196, 81, 0.8);
    }

    80% {
        box-shadow: 0 0 35px rgba(255, 196, 81, 0.7);
    }

    90% {
        box-shadow: 0 0 25px rgba(255, 196, 81, 0.6);
    }

    100% {
        box-shadow: 0 0 15px rgba(255, 196, 81, 0.5);
    }
}

@keyframes movingGlare {
    0% {
        left: -100%;
    }

    50% {
        left: -100%;
    }

    59% {
        left: 100%;
    }

    100% {
        left: 100%;
    }
}

.order-btn {
    background: linear-gradient(45deg, #ffc451, #000000);
    color: #f3f3d9;
    font-size: 13px;
    font-weight: bold;
    text-transform: uppercase;
    padding: 4px 13px;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    transition: 0.3s ease-in-out;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.4);
    position: relative;
    overflow: hidden;
    margin: 4px;
    animation: pulseGlare-af554db9 3s infinite ease-in-out;
    letter-spacing: 2px;
}

.order-btn::after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.2);
    animation: movingGlare 3s infinite ease-in-out;
}

.order-btn:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 15px rgba(255, 196, 81, 0.7);
}


.v-list-item {
    background: black;
    color: white;
}

a {
    text-decoration: none;
}

/* Logo */
.logo {
    margin: 0;
    padding: 0;
    line-height: 1;
    font-weight: 700;
    letter-spacing: 2px;
    text-transform: uppercase;
}

.logo a {
    color: #fff;
}

.logo a span {
    color: #ffc451;
}

.logo img {
    max-height: 50px;
    width: auto;
    transition: max-height 0.3s ease;
}

/* Main Menu */
ul.main-menu {
    margin: 0;
    padding: 0;
    display: flex;
    list-style: none;
    align-items: center;
}

ul.main-menu li {
    position: relative;
}

ul.main-menu a,
a:focus {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0px 12px;
    font-size: 16px;
    font-weight: 600;
    color: #fff;
    white-space: nowrap;
    transition: all 0.3s ease;
}

ul.main-menu a i,
ul.main-menu a:focus i {
    font-size: 12px;
    line-height: 0;
    margin-left: 5px;
}

ul.main-menu a:hover,
ul.main-menu .active,
ul.main-menu .active:focus,
ul.main-menu li:hover>a {
    color: #ffc451;
}


/* Mobile Menu */
.mobile-menu-item {
    text-align: center;
    /* Center the text horizontally */
}


/* Medium Desktop / Laptops (960px - 1200px) */
@media (min-width: 960px) and (max-width: 1200px) {
    .logo img {
        max-height: 42px;
    }

    ul.main-menu a {
        font-size: 13px;
        padding: 0px 6px;
    }

    .cart-header-icon,
    .social-share-icon {
        font-size: 24px !important;
    }

    .gold-amount-text {
        font-size: 0.82rem;
    }

    .popping-button {
        font-size: 20px;
        margin: 0 3px;
    }
}

/* Tablet (600px - 959px) */
@media (min-width: 600px) and (max-width: 959px) {
    .custom-app-bar {
        height: 60px !important;
        min-height: 60px !important;
        max-height: 60px !important;
    }

    .logo img {
        max-height: 38px;
    }

    ul.main-menu a {
        font-size: 11px;
        padding: 0px 4px;
    }

    .cart-header-icon,
    .social-share-icon {
        font-size: 22px !important;
    }

    .gold-amount-text {
        font-size: 0.75rem;
    }

    .popping-button {
        font-size: 19px;
        margin: 0 2px;
    }
}

.header-actions-group {
    margin-left: auto !important;
}

.custom-mobile-drawer {
    background: rgba(12, 12, 12, 0.98) !important;
    backdrop-filter: blur(14px) !important;
    border-bottom: 3px solid #ffc451 !important;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.95), 0 4px 16px rgba(255, 196, 81, 0.15) !important;
    z-index: 1005 !important;
    top: 72px !important;
    height: auto !important;
    max-height: calc(100vh - 72px) !important;
}

/* Tablet (600px - 959px) */
@media (min-width: 600px) and (max-width: 959px) {
    .custom-mobile-drawer {
        top: 60px !important;
        max-height: calc(100vh - 60px) !important;
    }
}

.mobile-drawer-item {
    transition: background 0.2s ease !important;
    border: none !important;
    border-bottom: 1px solid rgba(255, 196, 81, 0.35) !important;
    border-radius: 0 !important;
    margin: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
}

.mobile-drawer-item:last-child {
    border-bottom: none !important;
}

.mobile-drawer-item:hover,
.mobile-drawer-item:active {
    background: rgba(255, 196, 81, 0.15) !important;
    border-bottom: 1px solid rgba(255, 196, 81, 0.35) !important;
    transform: none !important;
    box-shadow: none !important;
}

.mobile-drawer-text {
    color: #ffffff !important;
    font-size: 0.95rem !important;
    letter-spacing: 1.5px !important;
    font-weight: 700 !important;
    transition: color 0.2s ease !important;
}

.mobile-drawer-item:hover .mobile-drawer-text,
.mobile-drawer-item:active .mobile-drawer-text {
    color: #ffc451 !important;
}

/* Phone / Extra Small (< 600px) */
@media (max-width: 599px) {
    .custom-mobile-drawer {
        top: 52px !important;
        max-height: calc(100vh - 52px) !important;
    }

    .custom-app-bar {
        height: 52px !important;
        min-height: 52px !important;
        max-height: 52px !important;
        padding: 0 6px !important;
    }

    .logo img {
        max-height: 30px;
    }

    .cart-header-icon,
    .social-share-icon {
        font-size: 20px !important;
    }

    .gold-amount-text {
        font-size: 0.72rem;
    }

    .popping-button {
        font-size: 18px;
        margin: 0 2px;
    }
}
</style>